import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCommunity, commApi } from './CommunityContext';
import { Avatar } from './CommunityShared';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Send, MessageCircle, Loader2, Anchor } from 'lucide-react';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

const CommunityMessages = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { member, loading } = useCommunity();

  useEffect(() => {
    if (!loading && !member) navigate('/community');
  }, [loading, member]);

  if (loading || !member) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(memberId ? '/community/messages' : '/community')} className="text-cyan-300 hover:text-white" data-testid="messages-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-cyan-400" /> Messaggi privati
          </h1>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {memberId ? <Thread otherId={memberId} /> : <ConversationList />}
      </div>
    </div>
  );
};

const ConversationList = () => {
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    commApi.get('/community/messages/conversations').then((r) => setConvs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-cyan-500 mx-auto my-10" />;

  if (convs.length === 0)
    return (
      <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed">
        <Anchor className="w-10 h-10 mx-auto mb-3 opacity-40" />
        Nessuna conversazione. Apri il profilo di un compagno di viaggio e scrivigli!
      </div>
    );

  return (
    <div className="space-y-2">
      {convs.map((c) => (
        <Card key={c.member.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/community/messages/${c.member.id}`)} data-testid={`conversation-${c.member.id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <Avatar member={c.member} size={46} />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-center">
                <p className="font-semibold text-slate-800 truncate">{c.member.name}</p>
                <span className="text-xs text-slate-400">{fmtTime(c.last_at)}</span>
              </div>
              <p className="text-sm text-slate-500 truncate">{c.last_text}</p>
            </div>
            {c.unread > 0 && <span className="bg-cyan-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{c.unread}</span>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Thread = ({ otherId }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  const load = async () => {
    try {
      const { data } = await commApi.get(`/community/messages/${otherId}`);
      setData(data);
    } catch (e) {
      toast.error('Conversazione non trovata');
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [otherId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await commApi.post(`/community/messages/${otherId}`, { text });
      setText('');
      load();
    } catch (e) {
      toast.error('Invio fallito');
    } finally {
      setSending(false);
    }
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-cyan-500 mx-auto my-10" />;

  return (
    <Card>
      <CardContent className="p-0">
        <div className="p-3 border-b flex items-center gap-3 bg-white">
          <Avatar member={data.member} size={40} />
          <p className="font-semibold text-slate-800">{data.member.name}</p>
        </div>
        <div className="h-[440px] overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="dm-thread">
          {data.messages.length === 0 && <p className="text-center text-slate-400 py-10">Inizia la conversazione 👋</p>}
          {data.messages.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] flex flex-col ${m.mine ? 'items-end' : 'items-start'}`}>
                <div className={`rounded-2xl px-3.5 py-2 ${m.mine ? 'bg-cyan-600 text-white rounded-tr-sm' : 'bg-white border border-slate-200 rounded-tl-sm'}`}>
                  <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                </div>
                <span className="text-[10px] text-slate-400 mt-0.5 px-1">{fmtTime(m.created_at)}</span>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t flex gap-2">
          <Input
            data-testid="dm-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
            placeholder="Scrivi un messaggio..."
          />
          <Button data-testid="dm-send-btn" onClick={send} disabled={sending} className="bg-cyan-600 hover:bg-cyan-700 px-4">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommunityMessages;
